package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Video
import org.jetbrains.compose.web.renderComposable

enum class AnalysisState { Idle, Running, Done, Error }

fun main() {
    registerMdComponents()
    initLocale()
    // Wire Firebase Analytics if Gradle generated a non-empty config
    // from local.properties (FIREBASE_* keys). Empty config → init is
    // skipped and every Analytics call becomes a no-op; the
    // AnalyticsEvents facade is still safe to call from anywhere.
    resolveFirebaseConfig()?.let { Analytics.init(it) }
    sendVisitBeacon()
    installGlobalErrorHandlers()
    renderComposable(rootElementId = "composeApp") {
        ProvideAppStrings {
            App()
        }
    }
}

/**
 * Tell the backend this browser opened the site.
 *
 * The SPA is served by its own nginx container, so a plain page load
 * never reaches the backend and would otherwise go uncounted. The
 * response carries the `wl_vid` cookie; deduplication happens
 * server-side, so calling this on every boot is correct and cheap.
 *
 * Fire-and-forget by design: a failed beacon must never affect boot.
 *
 * In production `jsApiBase()` is empty, making this a same-origin
 * request the browser sends and stores cookies for by default. In dev
 * the SPA runs on another port and `CorsConfig` deliberately keeps
 * `allowCredentials(false)`, so the cookie is not set locally — a known
 * gap documented in docs/visit-metrics.md, not a bug to work around.
 */
private fun sendVisitBeacon() {
    // `c=0` tells the backend the browser refuses cookies. Without it the
    // beacon exemption over-applies: a cookie-blocking client would be
    // counted as a brand-new visitor on every single page load instead of
    // landing in the separate blind-spot metric built for it.
    val cookiesOk = kotlinx.browser.window.navigator.cookieEnabled
    kotlinx.browser.window.fetch(
        "${jsApiBase()}/api/collect" + if (cookiesOk) "" else "?c=0",
        org.w3c.fetch.RequestInit(method = "POST"),
    ).catch { null }
}

/**
 * Catch JS errors that escape Compose's normal exception path:
 *
 *  - synchronous `throw`s from event handlers and external libs (the
 *    `error` event on `window`),
 *  - rejected promises that nothing awaits (the `unhandledrejection`
 *    event — Ktor callbacks, postMessage handlers, etc.).
 *
 * Each lands as an `app_exception` event in Analytics with a short
 * message and the source so we can spot regressions in production
 * without staring at user devtools.
 */
private fun installGlobalErrorHandlers() {
    val w = kotlinx.browser.window
    w.addEventListener("error", { raw ->
        val ev = raw.asDynamic()
        Analytics.recordError(
            kind = "window_error",
            message = ev.message as? String,
            context = mapOf(
                "file" to ((ev.filename as? String)?.take(120) ?: ""),
                "line" to ((ev.lineno as? Int) ?: 0),
            ),
        )
    })
    w.addEventListener("unhandledrejection", { raw ->
        val ev = raw.asDynamic()
        val reason = (ev.reason?.message as? String) ?: (ev.reason as? String)
        Analytics.recordError(kind = "unhandled_promise", message = reason)
    })
}

@Composable
private fun App() {
    // Single production AnalyticsEvents instance for the whole tree —
    // VM gets it via constructor, Composables via the CompositionLocal
    // below. Wrapping in `remember` means a recomposition doesn't
    // mint a fresh DefaultAnalyticsEvents (whose internal login-gate
    // state would otherwise reset and re-fire `login` on every paint).
    val analytics = remember { DefaultAnalyticsEvents() }
    val vm = remember { MainViewModel(analytics = analytics) }
    LaunchedEffect(Unit) { /* hook for future init effects */ }

    androidx.compose.runtime.CompositionLocalProvider(
        LocalPricing provides vm.pricing,
        LocalNudge provides { msg -> vm.showNudge(msg) },
        LocalAnalyticsEvents provides analytics,
    ) {
    NudgeSnackbar(vm.snackbarMessage)
    Div({
        style {
            bg(WLColors.Surface)
            fg(WLColors.OnSurface)
            property("min-height", "100vh")
            property("font-family", "system-ui, -apple-system, 'Segoe UI', sans-serif")
            flexCol()
            property("padding", "16px 0")
            property("box-sizing", "border-box")
        }
    }) {
        // Header
        Div({
            classes("wl-page-pad")
            style {
                fillWidth()
                property("box-sizing", "border-box")
            }
        }) {
            WritersLabTopBar(
                credits = vm.credits,
                fileName = if (vm.analysisState == AnalysisState.Done) vm.pickedFileName else null,
                wordCount = vm.analysisResult?.words ?: 0,
                isLoggedIn = vm.isLoggedIn,
                userName = vm.userName,
                onDownloadTxt = if (vm.analysisState == AnalysisState.Done) {{ vm.downloadTxt() }} else null,
                onReset = if (vm.analysisState == AnalysisState.Done) {{ vm.reset() }} else null,
                onSignIn = { vm.signIn() },
                onSignOut = { vm.signOut() },
                onTopUp = { vm.openTopUpDialog() },
                onHome = { vm.reset() },
            )
        }

        if (vm.isLoggedIn && !vm.emailVerified) {
            Div({
                classes("wl-page-pad")
                style { fillWidth(); property("box-sizing", "border-box") }
            }) {
                VerificationBanner(
                    email = vm.userName,
                    cooldownSeconds = vm.verificationCooldownSeconds,
                    onResend = { vm.sendVerificationEmail() },
                    onConfirm = { vm.confirmVerification() },
                )
            }
        }

        val pending = vm.pendingPayment
        if (pending != null && vm.pendingPaymentBannerVisible) {
            Div({
                classes("wl-page-pad")
                style { fillWidth(); property("box-sizing", "border-box") }
            }) {
                PendingPaymentBanner(
                    onResume = if (pending.redirectUrl.isNotBlank()) {{ vm.resumePendingCheckout() }} else null,
                    onHide = { vm.hidePendingPaymentBanner() },
                )
            }
        }

        // Landing hero — headline + subheadline, ABOVE the upload picker for
        // first-time visitors (anonymous / no books) so they read the value
        // prop and then drop a file right under it.
        if (vm.analysisState == AnalysisState.Idle && vm.bookHistory.isEmpty()) {
            CenteredColumn(maxWidthPx = 800) {
                Div({ style { property("height", "24px") } })
                LandingHero()
            }
        }

        // Upload picker (when not done). Hidden during Running so the only
        // place to look while analysis is in flight is the LoadingCard below —
        // without this guard the upload zone sat next to the spinner, inviting
        // a parallel upload and confusing whether anything had started.
        if (vm.analysisState == AnalysisState.Idle || vm.analysisState == AnalysisState.Error) {
            CenteredColumn(maxWidthPx = 800) {
                Div({ style { property("height", "24px") } })
                UploadArea(
                    onPickFile = { vm.pickFile() },
                    onDropFile = { vm.pickFileFromDrop(it) },
                )
                if (!vm.isLoggedIn && !vm.eulaAccepted) {
                    Div({ style { property("height", "8px") } })
                    EulaConsentRow(
                        checkedValue = vm.eulaChecked,
                        error = vm.eulaError,
                        onCheckedChange = { vm.setEulaChecked(it) },
                    )
                }
            }
        }

        // Landing supporting content — what you get, demos, examples, FAQ —
        // below the upload picker. First-time visitors only.
        if (vm.analysisState == AnalysisState.Idle && vm.bookHistory.isEmpty()) {
            CenteredColumn(maxWidthPx = 800) {
                Div({ style { property("height", "32px") } })
                LandingBelow()
            }
        }

        // Error banner
        if (vm.analysisState == AnalysisState.Error || vm.aiState == AnalysisState.Error) {
            // Count how often the error banner actually shows up to a
            // user — separate from the underlying ai_analysis_failed /
            // file_upload_failed events because not every failure
            // reaches this surface (some are swallowed by cancellation
            // / book switch), and we want to know which.
            val errorKind = if (vm.aiState == AnalysisState.Error)
                AnalyticsEvents.ErrorKind.AI_PIPELINE
            else
                AnalyticsEvents.ErrorKind.STATIC_ANALYSIS
            val analyticsForError = LocalAnalyticsEvents.current
            LaunchedEffect(errorKind, vm.errorMessage) {
                analyticsForError.errorShown(errorKind, vm.errorMessage)
            }
            CenteredColumn(maxWidthPx = 800) {
                Div({ style { property("height", "12px") } })
                ErrorCard(
                    message = vm.errorMessage,
                    onRetry = {
                        if (vm.aiState == AnalysisState.Error) {
                            vm.runAiAnalysis()
                        } else {
                            vm.clearError()
                        }
                    },
                )
            }
        }

        // Book history (or empty state for logged-in users with no books).
        if ((vm.analysisState == AnalysisState.Idle || vm.analysisState == AnalysisState.Error) &&
            vm.isLoggedIn
        ) {
            when {
                vm.bookHistory.isNotEmpty() -> CenteredColumn(maxWidthPx = 800) {
                    Div({ style { property("height", "16px") } })
                    BookHistoryList(vm.bookHistory) { item -> vm.selectFromHistory(item) }
                }
                vm.bookHistoryLoaded -> CenteredColumn(maxWidthPx = 800) {
                    Div({ style { property("height", "16px") } })
                    HistoryEmptyState()
                }
                else -> CenteredColumn(maxWidthPx = 800) {
                    Div({ style { property("height", "16px") } })
                    HistorySkeleton()
                }
            }
        }

        // Results
        Div({
            classes("wl-results-pad")
            style {
                fillWidth()
                property("padding", "20px 24px")
                property("box-sizing", "border-box")
                flexCol(gapPx = 16)
                property("align-items", "center")
            }
        }) {
            when (vm.analysisState) {
                AnalysisState.Running -> {
                    Div({ style { maxW(800) } }) {
                        LoadingCard("${LocalStrings.current.analyzing(vm.pickedFileName ?: "")}")
                    }
                }
                AnalysisState.Done -> {
                    vm.analysisResult?.let { result ->
                        // ResultsArea owns its own max-width — the wide
                        // layout (sidebar + main) needs ~1124px while the
                        // narrow falls back to 800px via the same CSS class.
                        ResultsArea(
                            result = result,
                            bookFilename = vm.pickedFileName,
                            bookId = vm.bookId,
                            isLoggedIn = vm.isLoggedIn,
                            // Same gate as the AI-analysis CTA (onRunAi): anon
                            // clicks on the highlight run buttons go to sign-in.
                            onRequireAuth = { vm.signIn() },
                            textEditing = TextEditing(
                                canEdit = vm.isLoggedIn && vm.canEditText,
                                editedAt = result.textEditedAt,
                                fileName = vm.pickedFileName,
                                onLockedShown = { vm.onTextEditLocked(it) },
                                onChoosePack = { vm.onChoosePackForEditing() },
                                onStarted = { vm.onTextEditStarted() },
                                onSaved = { res, changed -> vm.onTextSaved(res, changed) },
                                onSaveFailed = { vm.onTextSaveFailed(it) },
                                onDownloaded = { vm.onTextDownloaded() },
                                onRechecked = { cost, removed, added -> vm.onTextRechecked(cost, removed, added) },
                                onRecheckFailed = { vm.onTextRecheckFailed(it) },
                            ),
                            aiSidebarSlot = {
                                // Before the run this shows the same score card
                                // with dashes; after, the real scores. The run
                                // CTA lives in the right column (aiSlot).
                                AiScoreCard(vm.aiReport?.score?.takeIf { it.final != null })
                            },
                            aiSlot = {
                                // Keyed on bookId so internal remember-state (selectedTab)
                                // resets on book switch, matching promoSlot below.
                                androidx.compose.runtime.key(vm.bookId) {
                                    AiInsightsSection(
                                        aiState = vm.aiState,
                                        credits = vm.credits,
                                        isLoggedIn = vm.isLoggedIn,
                                        charCount = result.chars,
                                        onRunAi = {
                                            if (vm.isLoggedIn) vm.runAiAnalysis() else vm.signIn()
                                        },
                                        aiReport = vm.aiReport,
                                        aiStageName = vm.aiStageName,
                                        aiStage = vm.aiStage,
                                        aiTotalStages = vm.aiTotalStages,
                                        aiChunk = vm.aiChunk,
                                        aiTotalChunks = vm.aiTotalChunks,
                                        aiEtaSeconds = vm.aiEtaSeconds,
                                        aiElapsedSeconds = vm.aiElapsedSeconds,
                                        freeShortRemaining = vm.freeShortRemaining,
                                        freeShortCharThreshold = vm.freeShortCharThreshold,
                                        bare = true,
                                    )
                                }
                            },
                            promoSlot = {
                                // Keyed on bookId so local remember-state inside
                                // (form title/author/textMode, selectedTab, expanded,
                                // selectedHook on annotation) resets on book switch
                                // — otherwise the form fields from the previous book
                                // linger and read as "promo for the old book".
                                androidx.compose.runtime.key(vm.bookId) {
                                    PromoMaterialsSection(
                                        aiState = vm.aiState,
                                        aiReport = vm.aiReport,
                                        annotation = vm.annotationResult,
                                        annotationLoading = vm.annotationLoading,
                                        annotationError = vm.annotationError,
                                        onGenerateAnnotation = { vm.generateAnnotation() },
                                        covers = vm.coverResults,
                                        coverLoading = vm.coverLoading,
                                        coverError = vm.coverError,
                                        onGenerateAllCovers = { req -> vm.generateAllCovers(req) },
                                        onRegenerateCover = { v, req -> vm.regenerateCover(v, req) },
                                        teaser = vm.teaserResult,
                                        teaserLoadingKind = vm.teaserLoadingKind,
                                        teaserError = vm.teaserError,
                                        onTeaserPreview = { t, a -> vm.generateTeaserPreview(t, a) },
                                        onTeaserVideo = { t, a, lq -> vm.generateTeaserVideo(t, a, lq) },
                                        onTeaserRegenerate = { t, a -> vm.regenerateTeaserScene(t, a) },
                                        onTeaserDownload = { t -> vm.downloadTeaserVideo(t) },
                                        freeAnnotationsRemaining = vm.freeAnnotationsRemaining,
                                        freeCoverBatchesRemaining = vm.freeCoverBatchesRemaining,
                                        freeTeaserPreviewsRemaining = vm.freeTeaserPreviewsRemaining,
                                        bookId = vm.bookId,
                                        bare = true,
                                    )
                                }
                            },
                        )
                    }
                }
                AnalysisState.Idle, AnalysisState.Error -> {}
            }
        }

        // Footer
        Div({ style { property("flex", "1") } })
        Div({
            classes("wl-page-pad")
            style {
                border("1px", "solid", "rgba(74, 71, 84, 0.3)")
                property("border-width", "1px 0 0 0")
                property("margin-top", "32px")
                property("padding-top", "16px")
                property("padding-bottom", "24px")
                fillWidth()
                flexCol()
                property("align-items", "center")
                property("box-sizing", "border-box")
                property("text-align", "center")
            }
        }) {
            FooterContent()
        }
    }

    // Top up dialog (md-dialog manages its own open/close visibility)
    TopUpDialog(vm)

    // Post-checkout toast. VM raises one of three kinds once the watched
    // payment settles (or checkout fails); we render the matching string
    // and clear.
    val s = LocalStrings.current
    LaunchedEffect(vm.paymentToast) {
        when (val t = vm.paymentToast) {
            null -> {}
            is MainViewModel.PaymentToast.Success -> {
                Toaster.show(s.paymentSuccess(t.creditsAdded), ToastKind.Success)
                vm.clearPaymentToast()
            }
            MainViewModel.PaymentToast.Canceled -> {
                Toaster.show(s.paymentCanceled, ToastKind.Info)
                vm.clearPaymentToast()
            }
            MainViewModel.PaymentToast.Error -> {
                Toaster.show(s.paymentError, ToastKind.Error)
                vm.clearPaymentToast()
            }
        }
    }

    // Global toast host — single instance for the whole app.
    ToastHost()
    } // CompositionLocalProvider(LocalPricing)
}

@Composable
private fun NudgeSnackbar(message: String?) {
    if (message == null) return
    Div({
        classes("wl-nudge")
    }) { Text(message) }
}

@Composable
private fun CenteredColumn(maxWidthPx: Int, content: @Composable () -> Unit) {
    Div({
        style {
            fillWidth()
            flexCol()
            property("align-items", "center")
        }
    }) {
        Div({
            classes("wl-page-pad")
            style {
                maxW(maxWidthPx)
                flexCol()
                property("box-sizing", "border-box")
            }
        }) { content() }
    }
}

@Composable
private fun ErrorCard(message: String?, onRetry: (() -> Unit)? = null) {
    val s = LocalStrings.current
    Div({
        style {
            box(bgColor = "rgba(244, 112, 114, 0.1)", radiusPx = 8, paddingPx = 20)
            fillWidth()
            flexCol(gapPx = 12)
            property("align-items", "flex-start")
            property("box-sizing", "border-box")
        }
    }) {
        Span({
            style {
                fg(WLColors.Error)
                typography(TextLevel.BodyMedium)
                property("white-space", "pre-wrap")
                property("word-break", "break-word")
            }
        }) { Text(message ?: s.analysisFailed) }
        if (onRetry != null) {
            Div({
                style {
                    border("1px", "solid", WLColors.Error)
                    fg(WLColors.Error)
                    typography(TextLevel.LabelLarge)
                    property("padding", "6px 14px")
                    property("border-radius", "120px")
                    clickableCursor()
                }
                onClick { onRetry() }
            }) { Text(s.retryButton) }
        }
    }
}

@Composable
private fun HistorySkeleton() {
    Div({ style { fillWidth(); flexCol(gapPx = 6) } }) {
        // Section header placeholder.
        Div({
            classes("wl-skeleton")
            style {
                property("width", "180px")
                property("height", "14px")
                property("margin-bottom", "8px")
            }
        })
        repeat(3) {
            Div({
                style {
                    box(bgColor = WLColors.SurfaceContainerLow, radiusPx = 8, paddingPx = 0)
                    property("padding", "12px 16px")
                    flexRow(gapPx = 8)
                    fillWidth()
                    property("box-sizing", "border-box")
                }
            }) {
                Div({
                    classes("wl-skeleton")
                    style {
                        property("flex", "1")
                        property("height", "16px")
                    }
                })
                Div({
                    classes("wl-skeleton")
                    style {
                        property("width", "60px")
                        property("height", "14px")
                    }
                })
            }
        }
    }
}

@Composable
private fun HistoryEmptyState() {
    val s = LocalStrings.current
    Div({
        style {
            box(bgColor = WLColors.SurfaceContainerLow, radiusPx = 8, paddingPx = 24)
            fillWidth()
            flexCol(gapPx = 6)
            property("box-sizing", "border-box")
            property("text-align", "center")
            property("align-items", "center")
        }
    }) {
        Span({
            style {
                typography(TextLevel.TitleMedium)
                fg(WLColors.OnSurface)
            }
        }) { Text(s.historyEmptyTitle) }
        Span({
            style {
                typography(TextLevel.BodySmall)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text(s.historyEmptyHint) }
    }
}

@Composable
private fun LoadingCard(text: String) {
    Div({
        style {
            box(bgColor = WLColors.SurfaceContainerLow, radiusPx = 8, paddingPx = 24)
            fillWidth()
            flexRow(gapPx = 12)
            justifyContent(JustifyContent.Center)
            property("box-sizing", "border-box")
        }
    }) {
        Div({
            classes("wl-md-progress-sm")
            style { property("width", "24px"); property("height", "24px") }
        }) {
            MdCircularProgress(value = null, fourColor = false)
        }
        Span({
            style {
                typography(TextLevel.BodyMedium)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text(text) }
    }
}

@Composable
private fun BookHistoryList(items: List<BookHistoryItem>, onSelect: (BookHistoryItem) -> Unit) {
    val s = LocalStrings.current
    Div({ style { fillWidth(); flexCol(gapPx = 6) } }) {
        Span({
            style {
                typography(TextLevel.TitleSmall)
                fg(WLColors.OnSurfaceVariant)
                property("padding-bottom", "8px")
            }
        }) { Text(s.previousBooks) }
        items.forEach { item ->
            Div({
                style {
                    box(bgColor = WLColors.SurfaceContainerLow, radiusPx = 8, paddingPx = 0)
                    property("padding", "12px 16px")
                    flexRow(gapPx = 8)
                    property("flex-wrap", "wrap")
                    fillWidth()
                    clickableCursor()
                    property("box-sizing", "border-box")
                }
                onClick { onSelect(item) }
            }) {
                // Filename + word count are the primary line and stay together;
                // the word count never wraps on its own.
                Div({
                    style {
                        flexRow(gapPx = 8)
                        property("flex", "1 1 auto")
                        // Floor width so it's the badges — not the filename or
                        // word count — that wrap to a second line when tight.
                        property("min-width", "160px")
                    }
                }) {
                    Span({
                        style {
                            typography(TextLevel.BodyMedium)
                            fg(WLColors.OnSurface)
                            property("flex", "1")
                            property("min-width", "0")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        }
                    }) { Text(item.filename) }
                    Span({
                        style {
                            typography(TextLevel.BodySmall)
                            fg(WLColors.OnSurfaceVariant)
                            property("flex-shrink", "0")
                            property("white-space", "nowrap")
                        }
                    }) { Text("${item.wordCount} ${s.words}") }
                }
                // Only the badges wrap to the next line when the row is tight.
                Div({
                    style {
                        flexRow(gapPx = 8)
                        property("flex-shrink", "0")
                    }
                }) {
                    AiBadge(item.aiStatus)
                    SmartBadge(item.hasSmartHighlights)
                }
            }
        }
    }
}

/** One book-history status pill (label + hover tip). Shared by the badges
 *  below — they differ only in colour and text. */
@Composable
private fun StatusPill(label: String, tip: String, fgColor: String, bgColor: String) {
    Span({
        attr("title", tip)
        style {
            typography(TextLevel.LabelSmall)
            fg(fgColor)
            property("background-color", bgColor)
            property("border-radius", "120px")
            property("padding", "2px 6px")
        }
    }) { Text(label) }
}

/** Deep-analysis status pill in the book-history row. Violet. No "AI"/"ИИ"
 *  wording — the feature is «Глубокий анализ» / «Deep analysis». */
@Composable
private fun AiBadge(status: String) {
    val s = LocalStrings.current
    val (label, alpha) = when (status) {
        "done" -> s.badgeDeep to 0.15
        "running" -> "${s.badgeDeep}…" to 0.10
        "failed" -> "${s.badgeDeep} ✗" to 0.10
        else -> return
    }
    StatusPill(label, s.badgeDeepTip, WLColors.Primary, "rgba(165, 174, 247, $alpha)")
}

/** «Smart» pill — the smart stylistic highlighting has been run for this
 *  book (preview or full). Gold, to read as a distinct feature from the
 *  violet deep-analysis badge. */
@Composable
private fun SmartBadge(done: Boolean) {
    if (!done) return
    val s = LocalStrings.current
    StatusPill(s.badgeSmart, s.badgeSmartTip, WLColors.Secondary, "rgba(245, 185, 71, 0.15)")
}

/** Landing hero — headline + subheadline. Rendered above the upload picker so a
 *  first-time visitor reads the value prop, then drops a file right under it. */
@Composable
private fun LandingHero() {
    val s = LocalStrings.current
    Div({ style { fillWidth(); flexCol() } }) {
        Span({
            classes("wl-hero-headline")
            style {
                typography(TextLevel.HeadlineLarge)
                fg(WLColors.OnSurface)
                property("padding-bottom", "8px")
            }
        }) { Text(s.landingHeadline) }
        Span({
            classes("wl-hero-sub")
            style {
                typography(TextLevel.BodyLarge)
                fg(WLColors.OnSurfaceVariant)
            }
        }) { Text(s.landingSubheadline) }
    }
}

/** Landing supporting content shown BELOW the upload picker: what you get,
 *  demos, examples, FAQ. */
@Composable
private fun LandingBelow() {
    Div({ style { fillWidth(); flexCol() } }) {
        LandingFeatures()

        Div({ style { property("height", "24px") } })
        LandingDemoStrip()

        Div({ style { property("height", "16px") } })
        LandingExamples()

        Div({ style { property("height", "32px") } })
        LandingFaq()
    }
}

/** "What you get" section: an umbrella title over two columns (work on the
 *  text / prepare for publishing), each a card listing features with a
 *  one-line benefit. Reuses .wl-feature-row (2 columns, stacks on phones). */
@Composable
private fun LandingFeatures() {
    val s = LocalStrings.current
    Div({ style { fillWidth(); flexCol(gapPx = 14) } }) {
        Span({
            style {
                typography(TextLevel.HeadlineSmall)
                fg(WLColors.OnSurface)
            }
        }) { Text(s.landingFeaturesTitle) }
        Div({ classes("wl-feature-row") }) {
            FeatureColumn(s.landingFeaturesTextTitle, s.landingFeaturesTextItems)
            FeatureColumn(s.landingFeaturesPubTitle, s.landingFeaturesPubItems)
        }
    }
}

@Composable
private fun FeatureColumn(title: String, items: List<Pair<String, String>>) {
    Div({
        style {
            box(bgColor = WLColors.SurfaceContainerLow, radiusPx = 8, paddingPx = 18)
            flexCol(gapPx = 14)
            property("min-width", "0")
        }
    }) {
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.4px")
            }
        }) { Text(title) }
        items.forEach { (label, desc) ->
            Div({ style { flexCol(gapPx = 2) } }) {
                Span({ style { typography(TextLevel.TitleSmall); fg(WLColors.OnSurface) } }) { Text(label) }
                Span({ style { typography(TextLevel.BodyMedium); fg(WLColors.OnSurfaceVariant) } }) { Text(desc) }
            }
        }
    }
}

/** Text-example panels: a sales blurb (hook tagline + the opening
 *  paragraph from the annotation pipeline) and the opening of the
 *  literary review. Pulled verbatim from the «Around the World in 80
 *  Days» demo bundle in result/. These show what the non-visual
 *  deliverables actually look like — cover and teaser already cover
 *  the eye-candy in LandingDemoStrip. */
@Composable
private fun LandingExamples() {
    val s = LocalStrings.current
    Div({ style { fillWidth() } }) {
        Div({ classes("wl-feature-row") }) {
            ExamplePanel(s.landingExampleAnnotationLabel) {
                // Hook as the lead line — same size as body but heavier
                // weight, mirroring how book blurbs print the tagline.
                Span({
                    style {
                        typography(TextLevel.TitleSmall)
                        fg(WLColors.OnSurface)
                    }
                }) { Text(s.landingExampleAnnotationHook) }
                // Body is real annotation paragraph(s); .wl-example-clamp
                // truncates it with «…» at N lines so the panel doesn't
                // grow unbounded.
                Span({
                    classes("wl-example-clamp")
                    style {
                        typography(TextLevel.BodyMedium)
                        fg(WLColors.OnSurface)
                    }
                }) { Text(s.landingExampleAnnotationText) }
            }
            ExamplePanel(s.landingExampleReviewLabel) {
                // Tall-clamp because this panel has no hook line above
                // the body — needs more lines of its own to match the
                // annotation panel's total height.
                Span({
                    classes("wl-example-clamp-tall")
                    style {
                        typography(TextLevel.BodyMedium)
                        fg(WLColors.OnSurface)
                    }
                }) { Text(s.landingExampleReviewText) }
            }
        }
    }
}

@Composable
private fun ExamplePanel(label: String, content: @Composable () -> Unit) {
    Div({
        style {
            box(bgColor = WLColors.SurfaceContainerLow, radiusPx = 8, paddingPx = 18)
            flexCol(gapPx = 10)
            property("min-width", "0")
        }
    }) {
        Span({
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.4px")
            }
        }) { Text(label) }
        content()
    }
}

/** Demo strip showing real outputs from one analysis: a cover (left)
 *  and an autoplay teaser (right). Built from the «Around the World in
 *  80 Days» (Jules Verne) demo bundle in resources/demo/ — a PD classic
 *  so we never need a usage release from a real customer's manuscript,
 *  and the work itself is widely recognised both in RU and EU markets.
 *  Russian and English versions of the cover + teaser are shipped side
 *  by side; the active UI locale selects which pair the landing shows
 *  so an English visitor never sees a Cyrillic title overlay.
 *  The cells are width-capped in CSS (see .wl-demo-cover / .wl-demo-teaser)
 *  so the cover does not balloon to full viewport width on phones. */
@Composable
private fun LandingDemoStrip() {
    val isEnglish = currentAppLocale() == "en"
    val coverSrc = if (isEnglish) "demo/cover_en.png" else "demo/cover.png"
    val teaserSrc = if (isEnglish) "demo/teaser_en.mp4" else "demo/teaser.mp4"
    val posterSrc = if (isEnglish) "demo/teaser_en_poster.jpg" else "demo/teaser_poster.jpg"
    // key() forces the Img/Video to remount when the locale flips
    // mid-session — otherwise the browser keeps the cached element and
    // never re-fetches the new src, especially noticeable on <video>.
    androidx.compose.runtime.key(isEnglish) {
        Div({ classes("wl-demo-strip") }) {
            Div({ classes("wl-demo-cover") }) {
                Img(src = coverSrc, alt = "") {
                    style {
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                        property("display", "block")
                    }
                }
            }
            Div({ classes("wl-demo-teaser") }) {
                // Paused by default — landing already has enough motion,
                // autoplay on every visit becomes noise. Poster covers the
                // frame until the user explicitly hits play via the native
                // controls. preload=metadata keeps the mp4 off the wire
                // until interaction.
                Video({
                    attr("src", teaserSrc)
                    attr("poster", posterSrc)
                    attr("controls", "")
                    attr("playsinline", "")
                    attr("preload", "metadata")
                    style {
                        property("width", "100%")
                        property("height", "100%")
                        property("object-fit", "cover")
                        property("display", "block")
                    }
                })
            }
        }
    }
}

@Composable
private fun FooterContent() {
    val s = LocalStrings.current
    val emailSubject = s.supportEmailSubject
    val emailBody = s.supportEmailBody
    Span({
        style {
            typography(TextLevel.BodySmall)
            fg(WLColors.OnSurfaceVariant)
        }
    }) { Text(s.footerPowered) }
    Div({ style { property("height", "4px") } })
    Span({
        style {
            typography(TextLevel.LabelSmall)
            fg(WLColors.OnSurfaceVariant)
        }
    }) { Text(s.footerHint) }
    Div({ style { property("height", "12px") } })
    // Legal links + support, separated by faint dots. Wraps onto two lines
    // on phones thanks to flex-wrap.
    Div({
        style {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("justify-content", "center")
            property("align-items", "center")
            property("gap", "6px 14px")
            property("row-gap", "6px")
        }
    }) {
        FooterLink(s.privacyLink) {
            jsOpenInNewTab("https://writers-lab.what2read.space/privacy")
        }
        FooterDot()
        FooterLink(s.eulaLink.replaceFirstChar { it.uppercase() }) {
            jsOpenInNewTab("https://writers-lab.what2read.space/eula")
        }
        FooterDot()
        FooterLink("${s.supportContact} — info@what2read.space") {
            val subject = encodeURIComponent(emailSubject)
            val body = encodeURIComponent(emailBody)
            jsOpenInNewTab("mailto:info@what2read.space?subject=$subject&body=$body")
        }
    }
}

@Composable
private fun FooterLink(text: String, onClick: () -> Unit) {
    Div({
        style {
            typography(TextLevel.BodySmall)
            fg(WLColors.Primary)
            clickableCursor()
            property("padding", "4px 8px")
            property("border-radius", "6px")
        }
        onClick { onClick() }
    }) { Text(text) }
}

@Composable
private fun FooterDot() {
    Span({ style { fg(WLColors.OutlineVariant) } }) { Text("·") }
}

/**
 * Static catalogue of credit packs offered in the top-up dialog. Prices are
 * hard-coded for now — real billing integration will move this to the
 * server. Locale picks RUB or USD; everywhere else falls back to USD.
 */
data class CreditPack(
    /** Stable id matching the backend's CreditPacks catalogue. Sent to
     *  /api/payments/checkout — the server looks the price up by id so
     *  the displayed and charged amounts can't drift apart on the wire. */
    val id: String,
    val nameKey: (Strings) -> String,
    val credits: Int,
    val priceRub: Double,
    /** Display-only price for the intl region toggle. Real charging on
     *  the international rail is gated until the provider account has
     *  EUR balance enabled — until then intl clicks raise a stub toast
     *  and never reach the backend. Symbol shown is $. */
    val priceUsd: Double,
    val discountPct: Int,            // 0 means "no discount chip"
)

/**
 * Credit pack catalogue — prices come from [LocalPricing] (loaded from
 * prices.yml via GET /api/pricing on bootstrap). Composable so Compose
 * recomposes the pack cards automatically when pricing is refreshed.
 * USD prices are display-only; intl rail is not live yet.
 */
@Composable
private fun creditPacks(): List<CreditPack> {
    val p = LocalPricing.current
    return listOf(
        CreditPack("start",  { it.packStartName },  credits = p.packStartCredits,   priceRub = p.packStartPriceRub,  priceUsd = p.packStartPriceUsd,  discountPct = 0),
        CreditPack("medium", { it.packMediumName }, credits = p.packMediumCredits,  priceRub = p.packMediumPriceRub, priceUsd = p.packMediumPriceUsd, discountPct = 20),
        CreditPack("large",  { it.packLargeName },  credits = p.packLargeCredits,   priceRub = p.packLargePriceRub,  priceUsd = p.packLargePriceUsd,  discountPct = 40),
    )
}

// Pricing region toggle. 'ru' shows ₽ tier, 'intl' shows $ tier.
// Independent of the UI language so an EN-speaking RU resident or a
// RU-speaking expat can still see the right currency. Default follows
// the active locale (RU language → ru), persisted in localStorage on
// manual change.
private var pricingRegionState: String by androidx.compose.runtime.mutableStateOf("intl")
private var pricingRegionInitialised = false

private fun ensurePricingRegionInitialised() {
    if (pricingRegionInitialised) return
    pricingRegionInitialised = true
    pricingRegionState = AppDataStore.getPricingRegion()
        ?: detectRegionFromSystemLocale()
}

/**
 * Default region from `navigator.language` (system locale, NOT the
 * UI-language toggle). Russian system → 'ru'; everything else → 'intl'.
 * The user can override via the dialog header pills.
 */
private fun detectRegionFromSystemLocale(): String {
    val nav = kotlinx.browser.window.navigator
    val primary = nav.language.lowercase()
    if (primary.startsWith("ru")) return "ru"
    for (l in nav.languages) {
        if (l.lowercase().startsWith("ru")) return "ru"
    }
    return "intl"
}

fun currentPricingRegion(): String {
    ensurePricingRegionInitialised()
    return pricingRegionState
}

fun setPricingRegion(region: String) {
    if (region != "ru" && region != "intl") return
    pricingRegionState = region
    AppDataStore.setPricingRegion(region)
}

private fun Double.toFixed2(): String =
    (asDynamic().toFixed(2) as String).trimEnd('0').trimEnd('.')

private fun formatRub(n: Double): String {
    val repr   = n.toFixed2()
    val dotIdx = repr.indexOf('.')
    val digits = if (dotIdx >= 0) repr.substring(0, dotIdx) else repr
    val frac   = if (dotIdx >= 0) repr.substring(dotIdx) else ""
    val sb = StringBuilder()
    for ((i, c) in digits.reversed().withIndex()) {
        if (i > 0 && i % 3 == 0) sb.append('\u00A0')
        sb.append(c)
    }
    return sb.reverse().toString() + frac + "\u00A0\u20BD"
}

private fun formatUsd(n: Double): String = "$" + n.toFixed2()

@Composable
private fun CreditPacksRow(vm: MainViewModel) {
    val s = LocalStrings.current
    var selectedIdx by remember { mutableStateOf<Int?>(null) }
    val region = currentPricingRegion()
    val analytics = LocalAnalyticsEvents.current
    // No section heading — the dialog title plus the balance line above
    // already establish what the user is doing here. A second «top up
    // balance» label right above the pack cards repeats the same idea
    // and just adds visual noise.
    Div({ style { flexCol(gapPx = 6) } }) {
        Div({
            classes("wl-packs")     // three in a row, one per row on a phone
            style {
                // Locked while the tab leaves for the payment page.
                property("opacity", if (vm.checkoutInFlight) "0.6" else "1")
                property("transition", "opacity 0.15s ease")
            }
        }) {
            creditPacks().forEachIndexed { idx, pack ->
                PackCard(
                    pack = pack,
                    selected = idx == selectedIdx,
                    onClick = {
                        if (vm.checkoutInFlight) return@PackCard
                        analytics.packSelected(pack.id, region)
                        selectedIdx = idx
                        // Intl rail isn't wired live yet — provider account
                        // doesn't have EUR balance enabled. Short-circuit
                        // to a "contact support" stub toast so the user
                        // doesn't see a backend 502. RU rail (СБП) goes
                        // through the real checkout.
                        if (region == "intl") {
                            analytics.checkoutIntlStubbed(pack.id)
                            Toaster.show(s.packCheckoutStub, ToastKind.Info)
                            return@PackCard
                        }
                        // Region is read from the same toggle that drives
                        // the per-card price display — the backend looks
                        // up the matching priceRub from its own
                        // CreditPacks catalogue, so the charge always
                        // matches what the user just saw.
                        vm.startCheckout(pack.id, region, s.checkoutRedirecting)
                    },
                )
            }
        }
        if (vm.checkoutInFlight) {
            Span({
                style {
                    typography(TextLevel.BodySmall)
                    fg(WLColors.OnSurfaceVariant)
                }
            }) { Text(s.checkoutRedirecting) }
        }
    }
}

@Composable
private fun RegionToggle() {
    val s = LocalStrings.current
    val region = currentPricingRegion()
    Div({
        style {
            flexRow(gapPx = 2, align = AlignItems.Center)
            property("flex-shrink", "0")
        }
    }) {
        listOf("ru" to ("\u20BD" to s.regionRussia), "intl" to ("$" to s.regionInternational)).forEach { (key, payload) ->
            val (icon, ariaLabel) = payload
            val active = key == region
            Div({
                attr("role", "button")
                attr("aria-pressed", active.toString())
                attr("aria-label", ariaLabel)
                attr("title", ariaLabel)
                style {
                    property("font-size", "16px")
                    property("font-weight", "600")
                    property("line-height", "1")
                    if (active) {
                        bg(WLColors.PrimaryContainer)
                        fg(WLColors.OnPrimaryContainer)
                    } else {
                        bg(WLColors.SurfaceContainerHigh)
                        fg(WLColors.OnSurfaceVariant)
                    }
                    property("min-width", "30px")
                    property("padding", "6px 0")
                    property("text-align", "center")
                    property("border-radius", "120px")
                    clickableCursor()
                }
                onClick { setPricingRegion(key) }
            }) { Text(icon) }
        }
    }
}

@Composable
private fun PackCard(pack: CreditPack, selected: Boolean, onClick: () -> Unit) {
    val s = LocalStrings.current
    val region = currentPricingRegion()
    val priceText =
        if (region == "ru") formatRub(pack.priceRub)
        else formatUsd(pack.priceUsd)
    // Per-book price anchor. Multiplier `creditsPerAverageBook` is a
    // marketing-side average — NOT a recomputable number — so don't try
    // to derive it from Pricing.analysisCharsPerCredit. Source of truth
    // for the actual charge is the server's analysisCost(); this is a
    // display heuristic for "what does one book cost me?". Revisit when
    // observed median book length or pricing-per-credit shifts.
    val creditsPerAverageBook = 7.0
    val perBookRub = pack.priceRub * creditsPerAverageBook / pack.credits
    val perBookUsd = pack.priceUsd * creditsPerAverageBook / pack.credits
    // Whole rubles: it's an estimate, and «310.8» reads as a misprint.
    val perBookText =
        if (region == "ru") "≈ ${formatRub(kotlin.math.round(perBookRub))} ${s.packPerBook}"
        else "≈ ${formatUsd(perBookUsd)} ${s.packPerBook}"

    Div({
        // Layout from CSS: a column here, a two-line row on a phone.
        classes("wl-pack")
        style {
            // Bg10 (deeper than the dialog's Bg20) gives clear separation
            // without relying on the faint violet border alone.
            bg(WLColors.Surface)
            property("border-radius", "8px")
            property("padding", "10px")
            property("box-sizing", "border-box")
            property("min-width", "0")
            // Gold border = currently selected. Otherwise faint violet.
            property(
                "border",
                if (selected) "1px solid ${WLColors.Secondary}"
                else "1px solid rgba(165, 174, 247, 0.18)"
            )
            property("transition", "border-color 0.15s ease")
            clickableCursor()
        }
        onClick { onClick() }
    }) {
        // Top row: pack name + optional discount chip on the right.
        Div({
            classes("wl-pack-head")
            style {
                flexRow(gapPx = 6)
                fillWidth()
            }
        }) {
            Span({
                style {
                    typography(TextLevel.LabelLarge)
                    fg(WLColors.OnSurface)
                }
            }) { Text(pack.nameKey(s)) }
            if (pack.discountPct > 0) {
                Span({
                    style {
                        typography(TextLevel.LabelSmall)
                        bg("rgba(111, 207, 151, 0.18)")
                        fg(WLColors.Success)
                        property("padding", "1px 7px")
                        property("border-radius", "120px")
                        property("white-space", "nowrap")
                    }
                }) { Text(s.packDiscount(pack.discountPct)) }
            }
        }
        Span({
            classes("wl-pack-price")
            style {
                typography(TextLevel.TitleMedium)
                fg(WLColors.Secondary)
                property("white-space", "nowrap")
                property("margin-top", "2px")
            }
        }) { Text(priceText) }
        // Credit count + dragon icon — matches the top-bar credit chip
        // so the unit is consistent everywhere the user sees their
        // balance / a price tag. Dragon sized to match LabelSmall's
        // 13px cap height.
        Div({
            classes("wl-pack-credits")
            style {
                flexRow(gapPx = 4)
                property("white-space", "nowrap")
            }
        }) {
            Span({
                style {
                    typography(TextLevel.LabelSmall)
                    fg(WLColors.OnSurfaceVariant)
                }
            }) { Text(pack.credits.toString()) }
            DragonIcon(sizePx = 13, tint = DragonTint.GOLD)
        }
        Span({
            classes("wl-pack-perbook")
            style {
                typography(TextLevel.LabelSmall)
                fg(WLColors.OnSurfaceVariant)
                property("white-space", "nowrap")
            }
        }) { Text(perBookText) }
    }
}

@Composable
private fun TopUpDialog(vm: MainViewModel) {
    val s = LocalStrings.current
    val emailSubject = s.supportEmailSubject
    val emailBody = s.supportEmailBody
    val canRedeem = vm.redeemCodeInput.isNotBlank() && !vm.redeemInFlight
    val added = vm.redeemSuccessAdded
    val errorKey = vm.redeemError
    // Codes are hidden from regular users: Alt+Shift while the dialog is open
    // reveals them until it closes (latched, since the field needs typing).
    val altShift = rememberAltShiftHeld()
    var showRedeem by remember(vm.showTopUpDialog) { mutableStateOf(false) }
    LaunchedEffect(altShift, vm.showTopUpDialog) {
        if (altShift && vm.showTopUpDialog) showRedeem = true
    }
    // When a redeem succeeds, show a toast and close the dialog. The VM
    // resets `redeemSuccessAdded` in `closeTopUpDialog()`, so the effect
    // self-disarms.
    LaunchedEffect(added) {
        if (added != null) {
            Toaster.show(s.redeemSuccess(added), ToastKind.Success)
            vm.closeTopUpDialog()
        }
    }
    val errorText = when (errorKey) {
        null -> null
        "invalid_format" -> s.redeemErrorFormat
        "invalid_or_used" -> s.redeemErrorUsed
        else -> s.redeemErrorNetwork
    }
    MdDialog(
        open = vm.showTopUpDialog,
        onClose = { vm.closeTopUpDialog() },
        headline = s.topUpTitle,
        headlineTrailing = { RegionToggle() },
        actions = {
            Div({
                style {
                    typography(TextLevel.LabelLarge)
                    fg(WLColors.OnSurfaceVariant)
                    clickableCursor()
                    property("padding", "8px 16px")
                    property("border-radius", "6px")
                }
                onClick { vm.closeTopUpDialog() }
            }) { Text(s.close) }
            if (showRedeem) {
                Div({
                    style {
                        typography(TextLevel.LabelLarge)
                        if (canRedeem) {
                            bg(WLColors.Secondary)
                            fg(WLColors.OnSecondary)
                            clickableCursor()
                        } else {
                            bg(WLColors.OutlineVariant)
                            fg(WLColors.OnSurfaceVariant)
                        }
                        property("border-radius", "120px")
                        property("padding", "8px 18px")
                    }
                    onClick { if (canRedeem) vm.redeemCode() }
                }) { Text(s.redeemApply) }
            }
        },
    ) {
        Div({ style { flexCol(gapPx = 12) } }) {
            // Balance line: «Баланс: N 🐉». Word names the meaning, icon
            // labels the unit. The dash-legend variant («N 🐉 — credits»)
            // tried to teach the symbol inline but read awkwardly and
            // tripped Russian plural agreement (9 кредиты is wrong; 9
            // кредитов is right). Contextual repetition — dialog title,
            // pack CTA, the top-bar chip — does the teaching instead.
            Div({
                style {
                    flexRow(gapPx = 6)
                    typography(TextLevel.BodyLarge)
                    fg(WLColors.OnSurface)
                }
            }) {
                Span({ style { fg(WLColors.OnSurfaceVariant) } }) { Text("${s.balanceLabel}:") }
                Span({ style { fg(WLColors.OnSurface) } }) { Text(vm.credits.toString()) }
                DragonIcon(sizePx = 16, tint = DragonTint.GOLD)
            }

            // Buy-credits packs. Click kicks off a hosted-checkout
            // redirect via PaymentController — the user pays on the
            // provider's page and is sent back to /?payment=<id>.
            CreditPacksRow(vm)

            if (showRedeem) {
                MdOutlinedTextField(
                    value = vm.redeemCodeInput,
                    onValueChange = { vm.redeemCodeInput = it },
                    label = s.redeemCodeHint,
                    placeholder = "WL-XXXX-XXXX-XXXX",
                    disabled = vm.redeemInFlight,
                    error = errorKey != null,
                    errorText = errorText,
                    onSubmit = { if (canRedeem) vm.redeemCode() },
                )

                if (added != null) {
                    Span({
                        style { typography(TextLevel.BodySmall); fg(WLColors.Success) }
                    }) { Text(s.redeemSuccess(added)) }
                }
            }

            // Support link.
            Div({
                style {
                    property("border-top", "1px solid rgba(74, 71, 84, 0.3)")
                    property("padding-top", "12px")
                    property("margin-top", "4px")
                    flexCol(gapPx = 6)
                }
            }) {
                Div({
                    style {
                        typography(TextLevel.BodySmall)
                        fg(WLColors.Primary)
                        clickableCursor()
                        property("padding", "4px 6px")
                        property("border-radius", "6px")
                        property("align-self", "flex-start")
                    }
                    onClick {
                        val subject = encodeURIComponent(emailSubject)
                        val body = encodeURIComponent(emailBody)
                        jsOpenInNewTab("mailto:info@what2read.space?subject=$subject&body=$body")
                    }
                }) {
                    Text("${s.supportContact} — info@what2read.space")
                }
            }

            // Debug-only quick top-up.
            if (vm.debugTopUp) {
                Div({
                    style {
                        property("border-top", "1px solid rgba(74, 71, 84, 0.3)")
                        property("padding-top", "8px")
                    }
                }) {
                    Div({
                        style {
                            typography(TextLevel.LabelLarge)
                            fg(WLColors.Primary)
                            clickableCursor()
                            property("padding", "6px 8px")
                            property("border-radius", "6px")
                            property("align-self", "flex-start")
                            property("display", "inline-block")
                        }
                        onClick { vm.topUpCredits() }
                    }) { Text(s.topUpDebug) }
                }
            }
        }
    }
}
